/*
  Claire Powell collection grid (24 Oct 2026): filter drawer, sort and "Show 12 more" for
  sections/main-collection.liquid.

  The whole collection is already in the page, so everything happens here without reloading:
  - filters come from each card's own data (style, sizes in stock, price band, on sale); options with no
    matching products are disabled, and the drawer's button counts the results as she ticks;
  - sort reorders the cards (Featured is the collection's own order);
  - 12 cards show at first (the section's CSS does that before this file runs), then 12 more per click;
  - the view is kept in history.state, so Back from a product returns to the same filters, sort and length.
  A collection longer than one page has no filter, and its sort submits as a normal Shopify sort link.
*/
(() => {
  const grid = document.querySelector('[data-cpg]');
  if (!grid || grid.hasAttribute('data-cpg-ready')) return;
  grid.setAttribute('data-cpg-ready', '');

  const section = grid.closest('.cpg');
  const sortForm = section.querySelector('.cpg__sort');
  const sortSelect = sortForm ? sortForm.querySelector('select') : null;

  // The visible "Sort: …" text follows the hidden select
  const sortValue = sortForm ? sortForm.querySelector('.cpg__sort-value') : null;
  const showSort = () => {
    if (sortValue && sortSelect.selectedIndex >= 0) sortValue.textContent = sortSelect.options[sortSelect.selectedIndex].text;
  };

  if (grid.dataset.onePage !== 'true') {
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        showSort();
        sortForm.submit();
      });
    }
    return;
  }

  const FIRST = Number(grid.dataset.first) || 12;
  const nounOne = grid.dataset.nounOne || 'piece';
  const nounMany = grid.dataset.nounMany || 'pieces';
  const noun = (count) => (count === 1 ? nounOne : nounMany);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cards = Array.from(grid.querySelectorAll('.cpg__card')).map((el) => ({
    el,
    index: Number(el.dataset.index),
    style: el.dataset.style || '',
    price: Number(el.dataset.price) || 0,
    sizes: (el.dataset.sizes || '').split('|').filter(Boolean),
    sale: el.dataset.sale === 'true',
    added: Number(el.dataset.new) || 0
  }));

  const filterButton = section.querySelector('.cpg__filter');
  const filterCount = section.querySelector('.cpg__filter-count');
  const chips = section.querySelector('.cpg__chips');
  const more = section.querySelector('.cpg__more');
  const moreButton = section.querySelector('.cpg__more-btn');
  const status = section.querySelector('.cpg__status');
  const none = section.querySelector('.cpg__none');
  const drawer = document.getElementById(grid.dataset.drawer);
  const groupsBox = drawer ? drawer.querySelector('.cpf__groups') : null;
  const doneButton = drawer ? drawer.querySelector('[data-cpf-done]') : null;

  /* ---------- what can be filtered in this collection ---------- */

  const unique = (list) => list.filter((value, i) => value && list.indexOf(value) === i);
  const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);

  const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const sizeRank = (size) => {
    const rank = SIZE_ORDER.indexOf(size);
    return rank === -1 ? SIZE_ORDER.length : rank;
  };

  const BANDS = [
    { value: 'under-50', label: 'Under $50', test: (price) => price < 5000 },
    { value: '50-100', label: '$50 to $100', test: (price) => price >= 5000 && price < 10000 },
    { value: '100-150', label: '$100 to $150', test: (price) => price >= 10000 && price < 15000 },
    { value: '150-up', label: '$150 and over', test: (price) => price >= 15000 }
  ];

  const groups = [];

  const styles = unique(cards.map((card) => card.style));
  if (styles.length > 1) {
    groups.push({
      key: 'style',
      legend: 'Style',
      options: styles.map((style) => ({ value: style, label: capitalise(style) })),
      match: (card, value) => card.style === value
    });
  }

  const sizes = unique([].concat.apply([], cards.map((card) => card.sizes)));
  sizes.sort((a, b) => sizeRank(a) - sizeRank(b) || (a < b ? -1 : 1));
  if (sizes.length > 1) {
    groups.push({
      key: 'size',
      legend: 'Size',
      boxes: true,
      options: sizes.map((size) => ({ value: size, label: size })),
      match: (card, value) => card.sizes.indexOf(value) !== -1
    });
  }

  const bands = BANDS.filter((band) => cards.some((card) => band.test(card.price)));
  if (bands.length > 1) {
    groups.push({
      key: 'price',
      legend: 'Price',
      options: bands.map((band) => ({ value: band.value, label: band.label })),
      match: (card, value) => bands.some((band) => band.value === value && band.test(card.price))
    });
  }

  const saleTotal = cards.filter((card) => card.sale).length;
  const hasSale = saleTotal > 0 && saleTotal < cards.length;

  /* ---------- state: filters, sort and how many are showing ---------- */

  const SORTS = {
    '': (a, b) => a.index - b.index,
    'price-ascending': (a, b) => a.price - b.price || a.index - b.index,
    'price-descending': (a, b) => b.price - a.price || a.index - b.index,
    'created-descending': (a, b) => b.added - a.added || a.index - b.index
  };

  const blankState = () => ({ style: [], size: [], price: [], sale: false, sort: '', shown: FIRST });
  let state = blankState();
  state.sort = sortSelect && SORTS[sortSelect.value] ? sortSelect.value : '';

  const saved = window.history.state && window.history.state.cpg;
  if (saved && typeof saved === 'object') {
    groups.forEach((group) => {
      const known = group.options.map((option) => option.value);
      state[group.key] = (Array.isArray(saved[group.key]) ? saved[group.key] : []).filter((value) => known.indexOf(value) !== -1);
    });
    state.sale = hasSale && saved.sale === true;
    if (typeof saved.sort === 'string' && SORTS[saved.sort]) state.sort = saved.sort;
    if (Number(saved.shown) > FIRST) state.shown = Number(saved.shown);
    if (sortSelect) {
      sortSelect.value = state.sort;
      showSort();
    }
  }

  const save = () => {
    try {
      const cpg = { style: state.style, size: state.size, price: state.price, sale: state.sale, sort: state.sort, shown: state.shown };
      window.history.replaceState(Object.assign({}, window.history.state, { cpg }), '');
    } catch (error) {
      // Some browsers limit replaceState; the view still works, it just isn't remembered.
    }
  };

  const activeCount = () => groups.reduce((sum, group) => sum + state[group.key].length, 0) + (state.sale ? 1 : 0);

  // Does a card pass every filter, ignoring one group (for that group's own counts)?
  const matches = (card, except) =>
    groups.every((group) => group.key === except || state[group.key].length === 0 || state[group.key].some((value) => group.match(card, value))) &&
    (except === 'sale' || !state.sale || card.sale);

  /* ---------- the drawer ---------- */

  const make = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    return el;
  };

  let saleInput = null;
  let saleCount = null;

  function buildDrawer() {
    groups.forEach((group) => {
      const fieldset = make('fieldset', 'cpf__group');
      fieldset.appendChild(make('legend', 'cpf__legend', group.legend));
      const wrap = make('div', group.boxes ? 'cpf__sizes' : 'cpf__options');

      group.options.forEach((option, i) => {
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = `${drawer.id}-${group.key}-${i}`;
        input.value = option.value;
        input.dataset.group = group.key;

        const label = make('label', group.boxes ? 'cpf__size' : 'cpf__option');
        label.htmlFor = input.id;
        label.appendChild(input);

        if (group.boxes) {
          label.appendChild(make('span', '', option.label));
        } else {
          input.className = 'cpf__check';
          label.appendChild(make('span', 'cpf__label', option.label));
          option.count = make('span', 'cpf__count');
          label.appendChild(option.count);
        }

        option.input = input;
        option.labelEl = label;
        wrap.appendChild(label);
      });

      fieldset.appendChild(wrap);
      groupsBox.appendChild(fieldset);
    });

    if (hasSale) {
      const fieldset = make('div', 'cpf__group');
      const label = make('label', 'cpf__switch');
      const text = make('span', '', 'On sale only ');
      saleCount = make('span', 'cpf__count');
      text.appendChild(saleCount);
      saleInput = document.createElement('input');
      saleInput.type = 'checkbox';
      saleInput.setAttribute('role', 'switch');
      saleInput.dataset.group = 'sale';
      label.appendChild(text);
      label.appendChild(saleInput);
      fieldset.appendChild(label);
      groupsBox.appendChild(fieldset);
    }

    groupsBox.addEventListener('change', (event) => {
      const input = event.target;
      const key = input.dataset ? input.dataset.group : null;
      if (!key) return;

      if (key === 'sale') {
        state.sale = input.checked;
      } else {
        const values = state[key].filter((value) => value !== input.value);
        if (input.checked) values.push(input.value);
        state[key] = values;
      }

      state.shown = FIRST;
      render();
      save();
    });
  }

  function updateDrawer(total) {
    groups.forEach((group) => {
      group.options.forEach((option) => {
        const checked = state[group.key].indexOf(option.value) !== -1;
        const count = cards.filter((card) => matches(card, group.key) && group.match(card, option.value)).length;
        option.input.checked = checked;
        option.input.disabled = count === 0 && !checked;
        option.labelEl.classList.toggle('is-disabled', option.input.disabled);
        if (option.count) option.count.textContent = String(count);
      });
    });

    if (saleInput) {
      const count = cards.filter((card) => matches(card, 'sale') && card.sale).length;
      saleInput.checked = state.sale;
      saleInput.disabled = count === 0 && !state.sale;
      saleCount.textContent = `(${count})`;
    }

    if (doneButton) doneButton.textContent = total === 0 ? `No ${nounMany} match` : `Show ${total} ${noun(total)}`;
  }

  /* ---------- active filters under the bar ---------- */

  const CROSS = '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="m2 2 8 8M10 2l-8 8"/></svg>';

  function updateChips() {
    chips.textContent = '';
    const active = [];

    groups.forEach((group) => {
      state[group.key].forEach((value) => {
        const option = group.options.filter((item) => item.value === value)[0];
        active.push({ label: group.key === 'size' ? `Size ${option.label}` : option.label, remove: () => { state[group.key] = state[group.key].filter((item) => item !== value); } });
      });
    });
    if (state.sale) active.push({ label: 'On sale', remove: () => { state.sale = false; } });

    active.forEach((item) => {
      const chip = make('button', 'cpg__chip', item.label);
      chip.type = 'button';
      chip.setAttribute('aria-label', `Remove filter: ${item.label}`);
      chip.insertAdjacentHTML('beforeend', CROSS);
      chip.addEventListener('click', () => {
        item.remove();
        state.shown = FIRST;
        render();
        save();
        const next = chips.querySelector('.cpg__chip');
        if (next) next.focus();
        else if (filterButton) filterButton.focus();
      });
      chips.appendChild(chip);
    });

    if (active.length > 1) {
      const clear = make('button', 'cpg__chip cpg__chip--clear', 'Clear all');
      clear.type = 'button';
      clear.setAttribute('data-cpg-clear', '');
      chips.appendChild(clear);
    }

    chips.hidden = active.length === 0;
  }

  /* ---------- render ---------- */

  function render() {
    const ordered = cards.slice().sort(SORTS[state.sort] || SORTS['']);
    const results = ordered.filter((card) => matches(card));
    const visible = results.slice(0, Math.max(FIRST, state.shown));
    const total = results.length;

    // Put the cards in order (only when it changed) and show the first ones
    const current = Array.from(grid.children);
    if (ordered.some((card, i) => current[i] !== card.el)) ordered.forEach((card) => grid.appendChild(card.el));
    cards.forEach((card) => { card.el.hidden = visible.indexOf(card) === -1; });
    grid.classList.remove('cpg__grid--collapsed');

    const filtered = activeCount() > 0;
    none.hidden = total > 0;
    more.hidden = total === 0 || (total <= visible.length && !filtered);
    moreButton.hidden = total <= visible.length;
    const left = total - visible.length;
    moreButton.textContent = `Show ${Math.min(FIRST, left)} more ${noun(Math.min(FIRST, left))}`;
    if (total > visible.length) status.textContent = `Showing ${visible.length} of ${total} ${noun(total)}`;
    else status.textContent = total === 1 ? `Showing 1 ${nounOne}` : `Showing all ${total} ${nounMany}`;

    if (filterButton) {
      const count = activeCount();
      filterCount.textContent = count ? String(count) : '';
      filterButton.setAttribute('aria-label', count ? `Filter, ${count} active` : 'Filter');
    }

    if (drawer) updateDrawer(total);
    updateChips();
  }

  /* ---------- events ---------- */

  function scrollToResults() {
    const header = document.querySelector('.shopify-section--header');
    const bar = section.querySelector('.cpg__bar');
    const offset = (header ? header.offsetHeight : 0) + (bar ? bar.offsetHeight : 0) + 8;
    const top = grid.getBoundingClientRect().top + window.scrollY - offset;
    if (window.scrollY > top) window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  const clearFilters = () => {
    groups.forEach((group) => { state[group.key] = []; });
    state.sale = false;
    state.shown = FIRST;
    render();
    save();
  };

  document.addEventListener('click', (event) => {
    const clear = event.target.closest('[data-cpg-clear]');
    if (!clear || !(section.contains(clear) || (drawer && drawer.contains(clear)))) return;
    clearFilters();
    if (!drawer || !drawer.contains(clear)) {
      scrollToResults();
      if (filterButton) filterButton.focus({ preventScroll: true });
    }
  });

  if (sortSelect) {
    sortForm.addEventListener('submit', (event) => event.preventDefault());
    sortSelect.addEventListener('change', () => {
      // The server already sorted this page (a ?sort_by= link), so Featured needs the plain collection again
      if (grid.dataset.serverSorted === 'true' && sortSelect.value === '') {
        window.location.href = sortForm.getAttribute('action');
        return;
      }
      state.sort = sortSelect.value;
      showSort();
      render();
      save();
    });
  }

  moreButton.addEventListener('click', () => {
    const before = cards.filter((card) => !card.el.hidden).length;
    state.shown = Math.max(FIRST, state.shown) + FIRST;
    render();
    save();
    // Keyboard users carry on from the first new card
    const next = Array.from(grid.children).filter((el) => !el.hidden)[before];
    const link = next ? next.querySelector('a') : null;
    if (link) link.focus({ preventScroll: true });
  });

  // The section already shows Filter when there's something to filter; this only confirms it
  if (drawer && groupsBox && (groups.length > 0 || hasSale)) {
    buildDrawer();
    filterButton.hidden = false;
    doneButton.addEventListener('click', () => {
      Promise.resolve(drawer.hide ? drawer.hide() : null).then(scrollToResults);
    });
  } else if (filterButton) {
    filterButton.hidden = true;
  }

  render();
})();
